<ng-template #snapshotValidation>
  @if (snapshotFeedback) {
    <div role="alert" aria-live="assertive" aria-atomic="true">
      <p>{{ snapshotFeedback }}</p>
      @if (snapshotAffectedEmployees.length) {
        <ul>
          @for (employee of snapshotAffectedEmployees; track employee) {
            <li>{{ employee.name || 'Selected employee' }}</li>
          }
        </ul>
      }
      @if (snapshotAffectedEmployees.length) {
        <button mat-stroked-button type="button"
          [disabled]="isSavingTeam" (click)="deselectSnapshotAffected()">
          Deselect these {{ snapshotAffectedEmployees.length }}
        </button>
      }
    </div>
  }
</ng-template>

<app-step-dialog-shell
  [controller]="this"
  mode="sequential"
  [title]="dialogTitle"
  [subtitle]="dialogSubtitle"
  [showClose]="true"
  [disableClose]="isSavingTeam"
  (closeClicked)="close()"
  (cancelClicked)="close()"
  (primaryClicked)="save()"
  >
  <ng-template [stepDialogStep]="0">
    @if (data.snapshotMembers; as members) {
      <app-payroll-workforce-members [state]="members" [disabled]="isSavingTeam"></app-payroll-workforce-members>
      <ng-container *ngTemplateOutlet="snapshotValidation"></ng-container>
    } @else {
      @if (data.snapshotSubmit && !data.snapshotBrowse) {
        <p>{{ selectedMemberCount }} employees selected. Account matching and eligibility checks determine the final member count before the team is created.</p>
      }
      @if (data.snapshotSubmit && !data.snapshotBrowse) {
        <ng-container *ngTemplateOutlet="snapshotValidation"></ng-container>
      }
      <div class="field">
        <label class="field-label">Team name</label>
        <mat-form-field appearance="outline" class="full">
          <input matInput [formControl]="form.controls.teamName" />
          @if (form.controls.teamName.hasError('required')) {
            <mat-error>Team name is required</mat-error>
          }
        </mat-form-field>
      </div>
      <div class="field">
        <label class="field-label">Wellness benefit amount ($)</label>
        <mat-form-field appearance="outline" class="amount">
          <span matTextPrefix>$&nbsp;</span>
          <input
            matInput
            type="number"
            min="0"
            [max]="wellnessAmountMax"
            step="0.01"
            inputmode="decimal"
            [formControl]="form.controls.wellnessAmount"
            (input)="onWellnessAmountInput($event)"
            />
          @if (form.controls.wellnessAmount.hasError('required')) {
            <mat-error>Amount is required</mat-error>
          }
          @if (form.controls.wellnessAmount.hasError('max')) {
            <mat-error>
              Amount cannot exceed $999,999.99
            </mat-error>
          }
        </mat-form-field>
        <div class="helper">Monthly company-funded benefit amount per employee</div>
      </div>
    }
  </ng-template>

  <ng-template [stepDialogStep]="1">
    @if (data.snapshotBrowse; as snapshot) {
      <p>Select employees for this team. Account matching and eligibility checks determine the final member count.</p>
      @if (data.snapshotCriteria) {
        <mat-radio-group class="snapshot-selection-method" [(ngModel)]="snapshotMatching" (change)="onSnapshotSelectionModeChanged()" [disabled]="isSavingTeam" aria-label="Choose team members">
          <mat-radio-button [value]="false">Choose employees</mat-radio-button>
          <mat-radio-button [value]="true">Use team filters</mat-radio-button>
        </mat-radio-group>
      }
      <fieldset class="snapshot-selection" [disabled]="isSavingTeam">
        @if (snapshotMatching && data.snapshotCriteria; as criteria) {
          <p>Matching eligible employees will be checked when you create the team. These filters will be saved with the team. Automatic additions are not enabled.</p>
          <app-payroll-workforce-criteria [state]="criteria" [editorOnly]="true" [disabled]="isSavingTeam"></app-payroll-workforce-criteria>
          @if (criteria.snapshotId !== snapshot.context?.snapshot?.id) {
            <p role="alert">The workforce snapshot changed. Reopen this dialog to review team filters against the current employees.</p>
          }
          @if (isSavingTeam) {
            <p role="status">Checking matching employees and creating the team…</p>
          }
        } @else {
          <app-payroll-workforce-browse [state]="snapshot" [selectionOnly]="true" [disabled]="isSavingTeam"></app-payroll-workforce-browse>
        }
      </fieldset>
      <ng-container *ngTemplateOutlet="snapshotValidation"></ng-container>
    }
    @if (!data.snapshotBrowse) {
      <div class="step2">
        @if (hasMembersConnectionError) {
          <div class="payroll-connection-error">
            <div class="error-content">
              <mat-icon>error_outline</mat-icon>
              <div class="error-copy">
                <div class="error-title">Payroll connection error</div>
                <div class="error-message">{{ membersConnectionError }}</div>
              </div>
            </div>
            <button mat-raised-button color="primary" type="button" (click)="goToCompanyConnections()">
              Go to Company Connections
            </button>
          </div>
        }
        @if (!hasMembersConnectionError) {
          @if (fallbackBannerMessage) {
            <div class="payroll-fallback-banner">
              {{ fallbackBannerMessage }}
            </div>
          }
          @if (canShowManualMemberManagement) {
            <mat-expansion-panel
              class="manual-management-panel"
              [(expanded)]="isManualMemberManagementExpanded"
              >
              <mat-expansion-panel-header>
                <mat-panel-title>
                  Manual Member Management
                </mat-panel-title>
                <mat-panel-description>
                  {{ data.team.name || 'Current team' }}
                </mat-panel-description>
              </mat-expansion-panel-header>
              <div class="manual-management">
                <div class="manual-management__actions">
                  <mat-form-field appearance="outline" class="manual-management__email">
                    <mat-label>Email address</mat-label>
                    <input
                      matInput
                      type="email"
                      [(ngModel)]="manualMemberEmail"
                      placeholder="name@company.com"
                      (keyup.enter)="onAddFallbackTeamMember()"
                      />
                  </mat-form-field>
                  <button
                    mat-raised-button
                    color="primary"
                    type="button"
                    [disabled]="!manualMemberEmail.trim() || isAddingManualMember"
                    (click)="onAddFallbackTeamMember()"
                    >
                    Add by email
                  </button>
                </div>
                <div class="manual-management__upload">
                  <div class="manual-management__upload-copy">
                    Add members to <strong>{{ data.team.name || 'this team' }}</strong> with a CSV or XLSX file.
                    Supported column headers are
                    @for (column of teamMemberUploadColumns; track column; let last = $last) {
                      <strong>{{ column }}</strong>@if (!last) {
                      <span>, </span>
                    }
                    }.
                    @for (column of requiredUploadColumns; track column; let last = $last) {
                      <strong>{{ column }}</strong>@if (!last) {
                      <span>, </span>
                    }
                  }
                  {{ requiredUploadColumnVerb }} required.
                  <a target="_blank" [href]="uploadTemplateUrl">Download template</a>
                </div>
                <app-fileupload
                  requiredFileType=".csv,.xlsx"
                  (doneEvent)="onUploadFallbackTeamMembers($event)"
                ></app-fileupload>
              </div>
              @if (manualMemberUploadResult) {
                <div class="manual-management__result">
                  @if (manualMemberUploadResult.newCount !== undefined) {
                    <span>
                      {{ manualMemberUploadResult.newCount }} members added.
                    </span>
                  }
                  @if (manualMemberUploadResult.errorMessage) {
                    <span>
                      {{ manualMemberUploadResult.errorMessage }}
                    </span>
                  }
                </div>
              }
            </div>
          </mat-expansion-panel>
        }
        <div class="step2-header">
          <div class="step2-title">{{ canUseFilterDrivenMembersStep ? 'FILTER EMPLOYEE LIST' : 'SEARCH EMPLOYEE LIST' }}</div>
          <!-- Search -->
          <mat-form-field appearance="outline" class="search">
            <mat-icon matPrefix>search</mat-icon>
            <input
              matInput
              placeholder="Search employees by name or email..."
              [(ngModel)]="search"
              />
          </mat-form-field>
          <!-- Filter buttons row -->
          @if (canUseFilterDrivenMembersStep) {
            <div class="filter-row">
              @for (filter of availableFilters; track filter) {
                <mat-form-field appearance="outline" class="filter">
                  <mat-select
                    class="payroll-filter-select"
                    [placeholder]="filter[0]"
                    [value]="activeFiltersMap[filter[0]]"
                    (selectionChange)="handleFilterSelect(filter[0], $event.value)"
                    panelClass="payroll-filter-select-panel"
                    multiple
                    >
                    <mat-select-trigger>
                      <span class="filter-trigger">
                        <span class="filter-trigger-label">{{ filter[0] }}</span>
                        <span class="filter-trigger-count">{{ activeFilterCount(filter[0]) }}</span>
                      </span>
                    </mat-select-trigger>
                    @for (filterOption of filter[1]; track filterOption) {
                      <mat-option [value]="filterOption">
                        {{ displayFilterValue(filter[0], filterOption) }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              }
            </div>
          }
          <!-- Applied chips + Clear all -->
          @if (canUseFilterDrivenMembersStep && activeFilters.length) {
            <div class="applied-row">
              <mat-chip-listbox class="chips">
                @for (f of activeFilters; track f) {
                  <mat-chip-option
                    selected
                    removable
                    (removed)="clearFilter(f.key, f.value)"
                    >
                    <span class="chip-label">{{ f.label }}: {{ displayFilterValue(f.label, f.value) }}</span>
                    <button matChipRemove class="chip-x" aria-label="Remove filter">
                      <mat-icon>close</mat-icon>
                    </button>
                  </mat-chip-option>
                }
              </mat-chip-listbox>
              <button mat-button class="clear-all" (click)="clearAllFilters()">Clear all</button>
            </div>
          }
          <!-- Auto-add row -->
          @if (canShowAutoAddNewMembersControl) {
            <div class="autoadd-row">
              <mat-checkbox [(ngModel)]="autoAddNewMembers" [disabled]="!canEnableAutoAddNewMembers">
                Auto-add new Team Members
              </mat-checkbox>
              <button
                mat-icon-button
                [matTooltip]="autoAddNewMembersTooltip"
                aria-label="Info"
                >
                <mat-icon>help_outline</mat-icon>
              </button>
            </div>
          }
          @if (canShowAutoAddNewMembersControl && !canEnableAutoAddNewMembers) {
            <div class="autoadd-helper">
              {{ autoAddNewMembersDisabledReason }}
            </div>
          }
        </div>
        <!-- List header row -->
        <div
          class="employee-results-wrap"
          [class.is-loading]="isEmployeeResultsLoading"
          [attr.aria-busy]="isEmployeeResultsLoading"
          >
          @if (isEmployeeResultsLoading) {
            <div class="results-loading-overlay" aria-live="polite">
              <mat-spinner [diameter]="30"></mat-spinner>
              <div class="results-loading-copy">Applying filters...</div>
            </div>
          }
          <div class="list-meta">
            <div class="left">
              <mat-checkbox
                class="row-check"
                [checked]="allVisibleSelected"
                [indeterminate]="someVisibleSelected"
                (change)="toggleAllVisible($event.checked)"
                [disabled]="isEmployeeResultsLoading"
                aria-label="Select all visible employees"
              ></mat-checkbox>
              <span>Showing <b>{{ filteredEmployees.length }}</b> employees</span>
            </div>
            <div class="right">
              <span class="selected-pill">
                {{ selected.size }} selected
              </span>
            </div>
          </div>
          <mat-divider></mat-divider>
          <!-- Employee list -->
          <div class="employee-list">
            @for (e of filteredEmployees; track e) {
              <div
                class="employee-row"
                [class.selected]="isEmployeeSelected(e)"
                >
                <mat-checkbox
                  class="row-check"
                  [checked]="isEmployeeSelected(e)"
                  (change)="toggleEmployee(employeeKey(e), $event.checked)"
                  [disabled]="isEmployeeResultsLoading || !hasEmployeeEmail(e)"
                  [matTooltip]="missingEmployeeEmailTooltip(e)"
                ></mat-checkbox>
                <div class="emp-main">
                  <div class="emp-name">{{ e['First Name'] || e?.firstName }} {{ e['Last Name'] || e?.lastName }}</div>
                  @if (!hasEmployeeEmail(e)) {
                    <div
                      class="missing-email-alert"
                      [matTooltip]="missingEmployeeEmailTooltip(e)"
                      >
                      <mat-icon>warning_amber</mat-icon>
                      <span>Missing email address. Cannot import to Wallit.</span>
                    </div>
                  } @else {
                    <div class="emp-email">{{ employeeEmail(e) }}</div>
                  }
                  @if (employeeFilterSummary(e); as filterSummary) {
                    <div class="emp-sub">
                      {{ filterSummary }}
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>
  }
</ng-template>

<!-- FOOTER: STEP 1 -->
<ng-template [stepDialogFooter]="0">
  <button mat-stroked-button (click)="close()" [disabled]="isSavingTeam">Cancel</button>
  @if (hasMembersStep) {
    <button
      mat-raised-button
      color="primary"
      (click)="nextFromDetails()"
      [disabled]="isSavingTeam"
      >
      Next: Edit members
      <mat-icon class="btn-icon">chevron_right</mat-icon>
    </button>
  } @else {
    <button mat-raised-button color="primary" (click)="save()" [disabled]="isSavingTeam || snapshotSaveDisabled">
      {{ saveButtonLabel }}
    </button>
  }
</ng-template>

<!-- FOOTER: STEP 2 -->
<ng-template [stepDialogFooter]="1">
  <button mat-stroked-button (click)="backToDetails()" [disabled]="isSavingTeam">
    <mat-icon class="btn-icon-left">chevron_left</mat-icon>
    Back
  </button>

  <button mat-stroked-button (click)="close()" [disabled]="isSavingTeam">Cancel</button>
  <button mat-raised-button color="primary" (click)="save()" [disabled]="isSavingTeam || snapshotSaveDisabled">
    {{ saveButtonLabel }}
  </button>
</ng-template>
</app-step-dialog-shell>
